// src/services/api.service.js
import axios from 'axios';
import authService from './auth.service';
import { API_URL } from '../config';

// Create an axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor
apiClient.interceptors.request.use(
  async config => {
    const token = authService.getAccessToken();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    Promise.reject(error);
  }
);

// Add a response interceptor
apiClient.interceptors.response.use(
  response => {
    return response;
  },
  async error => {
    const originalRequest = error.config;
    
    // If the error is due to an expired token and we haven't tried to refresh yet
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Attempt to refresh the token
        const res = await authService.refreshToken();
        
        // If successful, update the authorization header and retry
        axios.defaults.headers.common['Authorization'] = `Bearer ${res.access}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // If refresh fails, logout the user and redirect to login
        authService.logout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

class ApiService {
  // Project endpoints
  getProjects() {
    return apiClient.get('projects/');
  }

  getProject(id) {
    return apiClient.get(`projects/${id}/`);
  }

  getProjectActivity(id) {
    return apiClient.get(`projects/${id}/activity/`);
  }

  getProjectSummary(id) {
    return apiClient.get(`projects/${id}/summary/`);
  }

  getCustomerSummary(id) {
    return apiClient.get(`customers/customers/${id}/summary/`);
  }

  createProject(project) {
    return apiClient.post('projects/', project);
  }

  updateProject(id, project) {
    // Always PATCH: quoted_amount is required on the model but masked from
    // roles without view_project_financials, so a PUT rebuilt from fetched
    // data would 400. Every caller already sends a partial or full field set.
    return apiClient.patch(`projects/${id}/`, project);
  }

  deleteProject(id) {
    return apiClient.delete(`projects/${id}/`);
  }
  getProjectAssignees() {
    return apiClient.get('auth/assignable-users/');
  }

  assignRoleToUser(userId, roleId) {
    return apiClient.post(`auth/users/${userId}/assign_role/`, { role_id: roleId });
  }

  // Comment endpoints
  getComments(projectId) {
    return apiClient.get(`comments/?project=${projectId}`);
  }

  createComment(comment) {
    return apiClient.post('comments/', comment);
  }

  // Payment endpoints
  getPayments(projectId) {
    return apiClient.get(`payments/?project=${projectId}`);
  }
  
  getAllPayments() {
    return apiClient.get('payments/');
  }

  getPayment(id) {
    return apiClient.get(`payments/${id}/`);
  }

  createPayment(payment) {
    return apiClient.post('payments/', payment);
  }

  // Role management endpoints
  getRoles() {
    return apiClient.get('auth/roles/');
  }

  getRole(id) {
    return apiClient.get(`auth/roles/${id}/`);
  }

  createRole(roleData) {
    return apiClient.post('auth/roles/', roleData);
  }

  updateRole(id, roleData) {
    return apiClient.put(`auth/roles/${id}/`, roleData);
  }

  deleteRole(id) {
    return apiClient.delete(`auth/roles/${id}/`);
  }

  assignPermissionsToRole(roleId, permissionIds) {
    return apiClient.post(`auth/roles/${roleId}/assign_permissions/`, {
      permission_ids: permissionIds
    });
  }

  // Permission endpoints
  getPermissions() {
    return apiClient.get('auth/permissions/');
  }

  // Application Types
  getApplicationTypes() {
    return apiClient.get('application-types/');
  }

  createApplicationType(data) {
    return apiClient.post('application-types/', data);
  }

  updateApplicationType(id, data) {
    return apiClient.put(`application-types/${id}/`, data);
  }

  deleteApplicationType(id) {
    return apiClient.delete(`application-types/${id}/`);
  }

  // Implementation Methods
  getImplementationMethods() {
    return apiClient.get('implementation-methods/');
  }

  createImplementationMethod(data) {
    return apiClient.post('implementation-methods/', data);
  }

  updateImplementationMethod(id, data) {
    return apiClient.put(`implementation-methods/${id}/`, data);
  }

  deleteImplementationMethod(id) {
    return apiClient.delete(`implementation-methods/${id}/`);
  }

  // Payment Methods
  getPaymentMethods() {
    return apiClient.get('payment-methods/');
  }


  createPaymentMethod(data) {
    return apiClient.post('payment-methods/', data);
  }

  updatePaymentMethod(id, data) {
    return apiClient.put(`payment-methods/${id}/`, data);
  }

  deletePaymentMethod(id) {
    return apiClient.delete(`payment-methods/${id}/`);
  }

  // Distributors
  getDistributors() {
    return apiClient.get('distributors/');
  }

  createDistributor(data) {
    return apiClient.post('distributors/', data);
  }

  updateDistributor(id, data) {
    return apiClient.put(`distributors/${id}/`, data);
  }

  deleteDistributor(id) {
    return apiClient.delete(`distributors/${id}/`);
  }

  // Invoices
  getProjectInvoices(projectId) {
    return apiClient.get(`invoices/?project=${projectId}`);
  }

  createInvoice(formData) {
    return apiClient.post(`invoices/`, formData, {
      headers: {
        // Important: DON'T set Content-Type manually when sending FormData
        'Content-Type': undefined  // This will let the browser set the correct multipart/form-data with boundary
      }
    });
  }

  settleInvoice(id, settledOn) {
    return apiClient.patch(`invoices/${id}/`, { settled_on: settledOn });
  }

  deleteInvoice(id) {
    return apiClient.delete(`invoices/${id}/`);
  }
  
  // Expense Categories
  getExpenseCategories() {
    return apiClient.get('expense-categories/');
  }
  
  createExpenseCategory(category) {
    return apiClient.post('expense-categories/', category);
  }
  
  updateExpenseCategory(id, category) {
    return apiClient.put(`expense-categories/${id}/`, category);
  }
  
  deleteExpenseCategory(id) {
    return apiClient.delete(`expense-categories/${id}/`);
  }
  
  reassignExpenses(sourceId, targetId) {
    return apiClient.post(`expense-categories/${sourceId}/reassign_expenses/`, {
      target_category: targetId
    });
  }
  
  // Expenses
  getExpenses(filters = {}) {
    let url = 'expenses/';
    const params = new URLSearchParams();
    
    if (filters.category) params.append('category', filters.category);
    if (filters.project) params.append('project', filters.project);
    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    return apiClient.get(url);
  }
  
  getExpense(id) {
    return apiClient.get(`expenses/${id}/`);
  }
  
  createExpense(expense) {
    if (expense.receipt instanceof File) {
      const formData = new FormData();
      Object.keys(expense).forEach(key => {
        formData.append(key, expense[key]);
      });
      
      return apiClient.post('expenses/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
    
    return apiClient.post('expenses/', expense);
  }
  
  updateExpense(id, expense) {
    if (expense.receipt instanceof File) {
      const formData = new FormData();
      Object.keys(expense).forEach(key => {
        if (expense[key] !== null) {
          formData.append(key, expense[key]);
        }
      });
      
      return apiClient.put(`expenses/${id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
    
    return apiClient.put(`expenses/${id}/`, expense);
  }
  
  deleteExpense(id) {
    return apiClient.delete(`expenses/${id}/`);
  }
  
  getExpenseSummary(groupBy = 'category') {
    return apiClient.get(`expenses/summary/?group_by=${groupBy}`);
  }

  // Provisionings with no project, across both products. Gated server-side on
  // view_company_financials; returns { count, results: [row] }.
  getUnbilledProvisionings() {
    return apiClient.get('provisioning/unbilled/');
  }

  // GymViz Provisioning endpoints
  getGymVizProvisionings(params = {}) {
    return apiClient.get('gymviz/provisioning/', { params });
  }

  getGymVizProvisioning(id) {
    return apiClient.get(`gymviz/provisioning/${id}/`);
  }

  createGymVizProvisioning(data) {
    return apiClient.post('gymviz/provisioning/', data);
  }

  updateGymVizProvisioning(id, data) {
    return apiClient.put(`gymviz/provisioning/${id}/`, data);
  }

  // AITIME Provisioning endpoints
  getProvisionings(params = {}) {
    return apiClient.get('aitime/provisioning/', { params });
  }
  
  getProvisioning(id) {
    return apiClient.get(`aitime/provisioning/${id}/`);
  }
  
  createProvisioning(data) {
    return apiClient.post('aitime/provisioning/', data);
  }
  
  updateProvisioning(id, data) {
    return apiClient.put(`aitime/provisioning/${id}/`, data);
  }
  
  deleteProvisioning(id) {
    return apiClient.delete(`aitime/provisioning/${id}/`);
  }
  
  deactivateProvisioning(id) {
    return apiClient.post(`aitime/provisioning/${id}/deactivate/`);
  }

  restoreProvisioning(id) {
    return apiClient.patch(`aitime/provisioning/${id}/restore/`);
  }
  
  regenerateLicense(id) {
    return apiClient.post(`aitime/provisioning/${id}/regenerate_license/`);
  }
  
  getDeploymentOptions() {
    return apiClient.get('aitime/provisioning/deployment_options/');
  }
  
  retrieveLicense(licenseId) {
    return apiClient.post('aitime/provisioning/retrieve_license/', { license_id: licenseId });
  }
  
  // New AITIME endpoints
  getLicenseByClientName(clientName) {
    return apiClient.get(`aitime/provisioning/get_license_by_client_name/?client_name=${encodeURIComponent(clientName)}`);
  }
  
  getLicenseByKey(licenseKey) {
    return apiClient.get(`aitime/provisioning/get_license_by_key/?license_key=${encodeURIComponent(licenseKey)}`);
  }
  
  markLicenseUsed(data) {
    // data should contain either { license_key: "..." } or { client_name: "..." }
    return apiClient.post('aitime/provisioning/mark_license_used/', data);
  }

  updateProvisioningStatus(id, status) {
    return apiClient.patch(`aitime/provisioning/${id}/update_status/`, { status });
  }

  // Download license file for offline installation
  downloadLicenseFile(id) {
    return apiClient.get(`aitime/provisioning/${id}/download_license/`, {
      responseType: 'blob' // Important: tells axios to expect binary data
    });
  }

  // Regenerate license file with current date
  regenerateLicenseFile(id) {
    return apiClient.post(`aitime/provisioning/${id}/regenerate_license_file/`, {}, {
      responseType: 'blob' // Important: tells axios to expect binary data
    });
  }

  // Customer endpoints
  getCustomers(params = {}) {
    return apiClient.get('customers/customers/', { params });
  }

  getCustomer(id) {
    return apiClient.get(`customers/customers/${id}/`);
  }

  createCustomer(data) {
    return apiClient.post('customers/customers/', data);
  }

  updateCustomer(id, data) {
    return apiClient.patch(`customers/customers/${id}/`, data);
  }

  deleteCustomer(id) {
    return apiClient.delete(`customers/customers/${id}/`);
  }

  getCustomerHistory(id) {
    return apiClient.get(`customers/customers/${id}/history/`);
  }

  // Sales follow-up endpoints
  getCustomerActivities(id) {
    return apiClient.get(`customers/customers/${id}/activities/`);
  }

  createCustomerActivity(id, data) {
    return apiClient.post(`customers/customers/${id}/activities/`, data);
  }

  getCustomerTasks(params = {}) {
    return apiClient.get('customers/tasks/', { params });
  }

  createCustomerTask(data) {
    return apiClient.post('customers/tasks/', data);
  }

  updateCustomerTask(id, data) {
    return apiClient.patch(`customers/tasks/${id}/`, data);
  }

  // Lead pipeline endpoints
  getLeadPipeline(params = {}) {
    return apiClient.get('customers/customers/pipeline/', { params });
  }

  moveCustomerStage(id, data) {
    return apiClient.post(`customers/customers/${id}/move_stage/`, data);
  }

  assignCustomerOwner(id, data) {
    return apiClient.post(`customers/customers/${id}/assign_owner/`, data);
  }

  getAssignableUsers() {
    return apiClient.get('customers/assignable-users/');
  }

  getCustomerOptions() {
    return apiClient.get('customers/options/');
  }

  // Reports endpoints
  getNearExpiry(days) {
    return apiClient.get('customers/near-expiry/', { params: { days } });
  }

  exportNearExpiry(days) {
    return apiClient.get('customers/near-expiry/export/', {
      params: { days },
      responseType: 'blob',
    });
  }

  getCustomerStatusBreakdown(params = {}) {
    return apiClient.get('customers/status-breakdown/', { params });
  }

  getRecentCustomers(limit) {
    return apiClient.get('customers/recent/', { params: { limit } });
  }

  getDistributorCustomerCounts() {
    return apiClient.get('customers/distributor-counts/');
  }

  getSalesMetrics(params = {}) {
    return apiClient.get('customers/metrics/', { params });
  }

  getSalesScoreboard(params = {}) {
    return apiClient.get('customers/scoreboard/', { params });
  }

  convertDemo(id, data) {
    return apiClient.post(`aitime/provisioning/${id}/convert_demo/`, data);
  }

  // Dashboard endpoints
  getDashboard(params = {}) {
    return apiClient.get('dashboard/', { params });
  }

  updateLastDashboard(preset) {
    return apiClient.patch('auth/me/', { last_dashboard: preset });
  }

  // Notification endpoints
  getNotifications(params = {}) {
    return apiClient.get('notifications/', { params });
  }

  markNotificationRead(id) {
    return apiClient.post(`notifications/${id}/read/`);
  }

  markAllNotificationsRead() {
    return apiClient.post('notifications/read-all/');
  }

  // API Token endpoints
  getAPITokens() {
    return apiClient.get('auth/api-tokens/');
  }

  getAPIToken(id) {
    return apiClient.get(`auth/api-tokens/${id}/`);
  }

  createAPIToken(data) {
    return apiClient.post('auth/api-tokens/', data);
  }

  deleteAPIToken(id) {
    return apiClient.delete(`auth/api-tokens/${id}/`);
  }

  activateAPIToken(id) {
    return apiClient.patch(`auth/api-tokens/${id}/activate/`);
  }

  deactivateAPIToken(id) {
    return apiClient.patch(`auth/api-tokens/${id}/deactivate/`);
  }

  toggleAPIToken(id) {
    return apiClient.patch(`auth/api-tokens/${id}/toggle_active/`);
  }

  // Issue endpoints
  getIssues(params = {}) {
    return apiClient.get('issues/', { params });
  }

  getIssue(id) {
    return apiClient.get(`issues/${id}/`);
  }

  createIssue(data) {
    return apiClient.post('issues/', data);
  }

  updateIssue(id, data) {
    return apiClient.patch(`issues/${id}/`, data);
  }

  deleteIssue(id) {
    return apiClient.delete(`issues/${id}/`);
  }

  getIssueComments(id) {
    return apiClient.get(`issues/${id}/comments/`);
  }

  addIssueComment(id, data) {
    return apiClient.post(`issues/${id}/comments/`, data);
  }

  // Not auth/assignable-users/: that one is gated on editing projects, which
  // support does not hold. This lists people who can work a ticket.
  getIssueAssignees() {
    return apiClient.get('issues/assignable-users/');
  }

  // Internal budget endpoints
  getBudgets(params = {}) {
    return apiClient.get('budgets/', { params });
  }

  createBudget(data) {
    return apiClient.post('budgets/', data);
  }

  updateBudget(id, data) {
    return apiClient.patch(`budgets/${id}/`, data);
  }

  deleteBudget(id) {
    return apiClient.delete(`budgets/${id}/`);
  }

  // A month's four numbers. The resource is a MONTH, keyed by its first day,
  // so there is no id and no create: a PUT to a month with no rows yet makes
  // them, and one request writes all four numbers in one transaction.
  getMonthTargets() {
    return apiClient.get('budgets/month-targets/');
  }

  /** PUT, not PATCH: a null clears that number, an omitted key leaves it be. */
  updateMonthTarget(periodStart, data) {
    return apiClient.put(`budgets/month-targets/${periodStart}/`, data);
  }

  getMonthTargetChanges(periodStart) {
    return apiClient.get(`budgets/month-targets/${periodStart}/changes/`);
  }
}

const apiServiceInstance = new ApiService();
export default apiServiceInstance;